<template>
  <v-app>
    <v-main>
      <pageLoader v-if="globalRouteLoading" />
      <pageHeader />
      <router-view />
      <MaintenanceOverlay
        :enabled="maintenanceEnabled"
        :logo="maintenanceLogo"
        :text="maintenanceText"
      />
      <v-snackbar
          v-model="snackbar"
          class="notificationAlert"
        >
          <router-link to="/NotificationPage">
            <div class="title">
              {{ this.toastTitle }}
            </div>
            <div class="body">
              {{ this.toastBody }}
            </div>
          </router-link>
        </v-snackbar>
      <pageFooter v-if="!hideFooter" />
      <addAdsPanner v-if="!hideFooter" />
    </v-main>
  </v-app>
</template>

<style>
/* Ensure SweetAlert is always on top of everything, including MaintenanceOverlay */
.swal2-container {
  z-index: 100000 !important;
}
.high-z-index-swal {
  z-index: 100001 !important;
}
</style>
<script>
import "./assets/css/style.css";
import pageHeader from "./components/Shared/pageHeader.vue";
import pageFooter from "./components/Shared/pageFooter.vue";
import MaintenanceOverlay from "./components/Shared/MaintenanceOverlay.vue";
import addAdsPanner from "./components/ads/addAdsPanner.vue";
import { resolveAsset } from "./utils/assets";
import pageLoader from "./components/Shared/pageLoader.vue";

import { messaging } from './firebase'
import { getToken, onMessage } from "firebase/messaging"
import { fullLogoutWipe, getGlobalRouteLoadingState } from "./utils/defaults";

export default {
  name: "App",
  components: {
    pageHeader,
    pageFooter,
    MaintenanceOverlay,
    addAdsPanner,
    pageLoader
  },
  data() {
    return {
      snackbar: false,
      toastTitle: "",
      toastBody: "",
      maintenanceEnabled: false,
      loginDisabled: false,
      maintenanceLogo: "",
      maintenanceText: "",
      maintenanceInterval: null,
      sessionCheckInterval: null,
      maintenanceLastFetchAt: 0,
      maintenanceInFlight: false,
      globalRouteLoading: getGlobalRouteLoadingState().globalRouteLoading
    };
  },
  computed: {
    hideFooter() {
      return !!(this.$route && this.$route.meta && this.$route.meta.hideFooter);
    }
  },
  methods:{
    normalizeLogo(src){
      if(!src) return "";
      // Keep base64/data URIs as-is
      if (/^(data:|blob:)/i.test(src)) return src;
      // Absolute URLs: rewrite via assets resolver to work through dev proxy
      if (/^https?:/i.test(src)) return resolveAsset(src);
      const trimmed = String(src).trim();
      
      const isDev = process.env.NODE_ENV === 'development';
      const prefix = isDev ? '/dashboard' : '';

      // Already pointing to storage path
      if (trimmed.startsWith('/storage')) {
        return prefix + trimmed; // ensure dev proxy handles it
      }
      if (trimmed.startsWith('storage/')) {
        return prefix + '/' + trimmed;
      }
      // Bare filename: assume settings images
      return prefix + '/storage/images/settings/' + trimmed.replace(/^\/+/, '');
    },
    async fetchMaintenanceFromAPI(){
      try{
        const nowTs = Date.now();
        if (this.maintenanceInFlight) return;
        if (nowTs - (this.maintenanceLastFetchAt || 0) < 15000) return;
        this.maintenanceLastFetchAt = nowTs;
        this.maintenanceInFlight = true;
        let res;
        // Use a direct axios call if possible, with a cache buster
        const ax = this.$axios || (await import('axios')).default;
        // Adding a timestamp to force fresh fetch and bypass any potential caching
        const url = `maintenance?t=${new Date().getTime()}`;
        
        res = await ax.get(url, { headers: { 'X-No-Global-Loader': true, 'X-Silent-Loading': true } });
        
        const data = res?.data?.data || res?.data || {};
        // Maintenance mode (full site block)
        this.maintenanceEnabled = !!(data.enabled);
        // Login disabled mode (specific user block)
        // Check both 'login_status' (common naming) and 'is_login_active' or similar if API differs
        // Based on user input, this is "Customer Login Status"
        this.loginDisabled = (data.login_status === false || data.login_status === 0 || data.login_status === '0');
        
        // Save to localStorage so Router can check it instantly
        localStorage.setItem('maintenance_enabled', this.maintenanceEnabled ? 'true' : 'false');
        localStorage.setItem('login_disabled', this.loginDisabled ? 'true' : 'false');

        // Force logout if maintenance/stop-login is enabled
        if (this.maintenanceEnabled || this.loginDisabled) {
          const token = sessionStorage.getItem('token');
          if (token) {
            fullLogoutWipe(ax);
            // Redirect to home if not already there, to ensure clean state
            if (this.$router && this.$router.currentRoute.value.path !== '/') {
                 this.$router.push('/').catch(() => {});
            }

            // Show logout alert
            this.$swal({
              icon: "warning",
              title: "تم تسجيل الخروج",
              text: "تم إيقاف تسجيل الدخول مؤقتاً من قبل الإدارة",
              confirmButtonText: "حسناً",
              allowOutsideClick: false,
              backdrop: true,
              customClass: {
                container: 'high-z-index-swal'
              }
            });
          }
        }
        
        // Handle logo update specifically
        const newLogo = this.normalizeLogo(data.logo || '');
        if (this.maintenanceLogo !== newLogo) {
             this.maintenanceLogo = newLogo;
        }
        
        this.maintenanceText = data.text || '';
      }catch(_){
        return;
      } finally {
        this.maintenanceInFlight = false;
      }
    },
    async requestPermission(){
      // Guard: skip prompting if Notifications not supported or already denied
      if (typeof Notification === 'undefined') return;
      const current = Notification.permission;
      if (current === 'denied') return; // don't spam prompts or logs
      let permission = current;
      if (current === 'default') {
        try {
          permission = await Notification.requestPermission();
        } catch (_) {
          return;
        }
      }
      if( permission === "granted" ){
        if (!messaging) return;

        // console.log('granted')
        // Generate token
        // we get the token from project setting => cloud messaging => generateKey
        getToken(messaging, { usePublicVapidKey: "BEsAV6wTLeQAm392UkC7w0algA7iDPN0jczpHEi1J7JUBhyx4aUHBeGV6tusRtyBK3wSEblWfdeoifiWzU7eXko" })
        // {vapidKey:"BFpjV9Ma8fIm3fnaCxRZMuQM_iPkZcyUpmje05C7sG-S7K7MNcep0DLwwim9mKV0w6hyLKaPtyHQDiXlJBol64w"}
        .then((currentToken) => {
          if (currentToken) {
            localStorage.setItem('FCMToken', currentToken.toString());
          } else {
            this.snackbar = false;
          }
        }).catch(() => {
          this.snackbar = false;
        });


        //To handle foreground messages
        onMessage(messaging, (message) => {
          this.toastTitle = message.notification.title
          this.toastBody = message.notification.body
          this.snackbar = true

          // Refresh the customer notification counter when a new notification arrives
          window.dispatchEvent(new Event('notifications:changed'));
        })



      }else if( permission === "denied" ){
          this.snackbar = false;
      }
    }
  },
  created(){
    // Only initialize notifications without prompting
    this.requestPermission();
  },

  // beforeCreate() {

  //   if (sessionStorage.getItem("lang")) {
  //     this.$i18n.locale = sessionStorage.getItem("lang");
  //   }
  // },

  mounted() {
      // document.querySelector("html").lang = sessionStorage.getItem("lang")
      // Fetch immediately when tab gains focus or becomes visible
      this.fetchMaintenanceFromAPI();
      
      window.addEventListener('focus', () => {
        this.fetchMaintenanceFromAPI();
      });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.fetchMaintenanceFromAPI();
        }
      });
      
      // Global click listener to force check status on any interaction
      document.addEventListener('click', (e) => {
        const loginDisabled = localStorage.getItem('login_disabled') === 'true';
        const maintenanceEnabled = localStorage.getItem('maintenance_enabled') === 'true';
        const token = sessionStorage.getItem('token');
        
        if ((loginDisabled || maintenanceEnabled) && token) {
            e.preventDefault();
            e.stopPropagation();
            
            // Force logout immediately
            fullLogoutWipe(this.$axios);
            
            // Redirect
            if (this.$router && this.$router.currentRoute.value.path !== '/') {
                 this.$router.push('/').catch(() => {});
            }
            
            // Show alert
            this.$swal({
              icon: "warning",
              title: "تم تسجيل الخروج",
              text: "تم إيقاف تسجيل الدخول مؤقتاً من قبل الإدارة",
              confirmButtonText: "حسناً",
              allowOutsideClick: false,
              backdrop: true,
              customClass: {
                container: 'high-z-index-swal'
              }
            });
        }
      }, true); // Capture phase to intercept early

      this.maintenanceInterval = setInterval(() => {
        this.fetchMaintenanceFromAPI();
      }, 30000);

      const axPromise = (async () => this.$axios || (await import('axios')).default)();
      let sessionCheckTimer = null;
      const runSessionCheck = async () => {
        const token = sessionStorage.getItem('token');
        if (!token) return;
        try {
          const ax = await axPromise;
          await ax.get('profile', { headers: { 'X-No-Global-Loader': true, 'X-Silent-Loading': true } });
        } catch (_) {
          void 0;
        } finally {
          const nextMs = document.hidden ? 8000 : 2000;
          sessionCheckTimer = setTimeout(runSessionCheck, nextMs);
        }
      };
      sessionCheckTimer = setTimeout(runSessionCheck, 2000);
      this.sessionCheckInterval = {
        __type: 'timeout-loop',
        clear: () => {
          if (sessionCheckTimer) clearTimeout(sessionCheckTimer);
          sessionCheckTimer = null;
        }
      };
  },
  beforeUnmount() {
      if (this.maintenanceInterval) {
        clearInterval(this.maintenanceInterval);
        this.maintenanceInterval = null;
      }
      if (this.sessionCheckInterval) {
        if (typeof this.sessionCheckInterval === 'number') {
          clearInterval(this.sessionCheckInterval);
        } else if (this.sessionCheckInterval && typeof this.sessionCheckInterval.clear === 'function') {
          this.sessionCheckInterval.clear();
        }
        this.sessionCheckInterval = null;
      }
  }

};
</script>
